// conventions-allow: file-size — this existing owner coordinates the DA management feature seams.
import { Tooltip } from '@mui/material'

import { useDatasets } from 'api/dataset/dataset.api'
import {
  useCopyBidsFromDate,
  useElplanBidCreateMutation,
  useElplanBidUpdateMutation,
  useElplanInfo,
  useElplanSendingBidMutation,
} from 'api/elplan/elplan.api'
import { resolveElplanBidsQueryEndTime } from 'api/elplan/elplan.bidQueryWindow.helper'
import { ELPLAN_BID_REFRESH_EVENT_QUERY_KEY } from 'api/elplan/elplan.constants'
import { BidTypeName } from 'api/elplan/elplan.types'
import { postStat } from 'api/stats/stats.api'
import { useUiConfigAnchorComponent } from 'api/uiConfig/uiConfig.api'
import { getCalculatedDataset } from 'helpers/dataset.helper/dataset.calc.helper'
import { getDatasetInstructionsForSumVolumeCalc, getItemsForExcel } from 'helpers/elplan.helper/bidDataset.helper'
import { exportPlannedElectricityProductionToExcel } from 'helpers/elplan.helper/elplanExport.helper'
import React, { ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import elplanStore from 'store/elplan/elplan'
import { colors } from 'styles/variables'
import { Card } from 'ui/atoms'
import NewButton from 'ui/atoms/NewButton/NewButton'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import { BidSubmitConfirmDialog } from 'ui/components/BidSubmitConfirmDialog/BidSubmitConfirmDialog'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import TooltipIconButton from 'ui/components/TooltipIconButton/TooltipIconButton'
import UiConfig from 'ui/uiConfig/UiConfig'
import Datetime from 'utils/datetime/datetime'
import { useSnapshot } from 'valtio'
import BidModal from 'views/ElectricityTradingView/components/shared/DayAheadBidEditor/BidModal/BidModal'
import CopyBidsModal from 'views/ElectricityTradingView/components/shared/DayAheadBidEditor/CopyBidsModal/CopyBidsModal'
import { DayAheadBidEditor } from 'views/ElectricityTradingView/components/shared/DayAheadBidEditor/DayAheadBidEditor'
import { useDayAheadBidTagSelectionContext } from 'views/ElectricityTradingView/components/shared/DayAheadBidEditor/useDayAheadBidTagSelectionContext'
import type { FormattedBid, FormattedBidWithIdAndTitle } from '../../DayAheadTradingView.types'
import { BidActivityBanner } from '../BidActivityBanner/BidActivityBanner'
import { ClearBidsAction } from '../ClearBidsAction/ClearBidsAction'
import { DayAheadBidInputSurface } from '../DayAheadBidTable/DayAheadBidInputSurface'
import { serializeDayAheadBidTableContext } from '../DayAheadBidTable/DayAheadBidTable.context.helper'
import type { DayAheadBidTableContext } from '../DayAheadBidTable/DayAheadBidTable.types'
import { useDayAheadBidTableResetController } from '../DayAheadBidTable/useDayAheadBidTableResetController'
import { useHasBidsOutsideSelectedPartition } from '../DayAheadPriceScenarios/useHasBidsOutsideSelectedPartition'
import {
  buildDayAheadBidCopyPayload,
  buildDayAheadBidCreatePayload,
  buildDayAheadBidMutation,
} from './DayAheadBidManagement.helper'
import styles from './DayAheadBidManagement.module.less'

type DayAheadBidManagementProps = {
  selectedPartitionKey?: string
}

type BidCopyRequest = {
  bidType: BidTypeName
  partitionId?: number
}

export function DayAheadBidManagement({ selectedPartitionKey = '' }: DayAheadBidManagementProps): ReactNode {
  const elplanSnap = useSnapshot(elplanStore)
  const { t } = useTranslation()
  const auth = useAuth()
  const activeSystem = auth.activeSystem
  const isSuperUser = auth.user?.is_superuser === true
  const [isBidConfirmOpen, setIsBidConfirmOpen] = useState(false)
  const clearResetController = useDayAheadBidTableResetController()
  const selectedStartTime = elplanSnap.date.start_time ?? Datetime.getTomorrowDate().startTime
  const selectedEndTime = elplanSnap.date.end_time ?? Datetime.getTomorrowDate().endTime
  const selectedDate = {
    startTime: selectedStartTime,
    endTime: selectedEndTime,
  }

  const currentView = elplanSnap.date.current_view ?? 'tomorrow'
  const disabledBidButtons = currentView === 'optimizationPeriod'

  const [bidCopyRequest, setBidCopyRequest] = useState<BidCopyRequest>()
  const [currentBlockBid, setCurrentBlockBid] = useState<FormattedBidWithIdAndTitle>()

  const [currentProfileBid, setCurrentProfileBid] = useState<FormattedBidWithIdAndTitle>()

  const systemHasBlockBids = elplanSnap.bid_types.some((bid) => bid.type === 'blockbid')
  const systemHasProfileBids = elplanSnap.bid_types.some((bid) => bid.type === 'profilebid')
  const amountOfBlockBids = elplanSnap.blockBidInfo.amount || 0
  const amountOfProfileBids = elplanSnap.profileBidInfo.amount || 0

  // Datasets for excel export of bids
  const onlyReturnIds = useMemo(() => {
    const onlyReturnIds = ['spotbid_without_price.volume']

    if (systemHasBlockBids) {
      for (let i = 0; i < amountOfBlockBids; i++) {
        onlyReturnIds.push(`blockbid.${i + 1}.volume`)
        onlyReturnIds.push(`blockbid.${i + 1}.price`)
      }
    }

    if (systemHasProfileBids) {
      for (let i = 0; i < amountOfProfileBids; i++) {
        onlyReturnIds.push(`profilebid.${i + 1}.volume`)
        onlyReturnIds.push(`profilebid.${i + 1}.price`)
      }
    }

    return onlyReturnIds
  }, [amountOfBlockBids, amountOfProfileBids, systemHasBlockBids, systemHasProfileBids])

  const { data: elplanBidsUiConfig } = useUiConfigAnchorComponent('elplan_bids')
  const { data: elplanInfo } = useElplanInfo(auth.systemId)
  const partitionId = elplanInfo?.partitions.find(({ key }) => key === selectedPartitionKey)?.id
  const tableContext: DayAheadBidTableContext = {
    elplanInfoId: elplanSnap.elplan_info_id,
    startTime: selectedStartTime,
    endTime: selectedEndTime,
    gridIdentity: 'day_ahead_bid_management',
    scenarioId: undefined,
    partitionId,
  }
  const tableContextKey = serializeDayAheadBidTableContext(tableContext)
  const sendIncludesBidsOutsideSelectedPartition = useHasBidsOutsideSelectedPartition({
    elplanInfoId: elplanSnap.elplan_info_id,
    startTime: selectedStartTime,
    endTime: resolveElplanBidsQueryEndTime(selectedEndTime),
    selectedPartitionId: partitionId,
  })
  const { data: elplanBidsDatasets = [] } = useDatasets(
    elplanBidsUiConfig?.id ?? -1,
    elplanBidsUiConfig?.version ?? -1,
    elplanBidsUiConfig?.dataset_instructions || [],
    selectedStartTime,
    resolveElplanBidsQueryEndTime(selectedEndTime),
    {
      onlyReturnIds,
      fillMissingHourTimestamps: false,
      ensureUniformTimeIntervals: false,
      datasetRefreshToken: ELPLAN_BID_REFRESH_EVENT_QUERY_KEY,
      overrideAlias: {
        elplan_info_id: elplanSnap.elplan_info_id,
        ...(partitionId === undefined ? {} : { elplan_partition_id: partitionId }),
      },
    }
  )

  const itemsForExcel = getItemsForExcel(amountOfBlockBids)
  const itemsDataId: Set<string> = new Set(itemsForExcel.map((item) => item.data_id))
  const visibleDatasets = elplanBidsDatasets.filter(({ return_id }) => itemsDataId.has(return_id))
  const datasetNames = (itemsForExcel || []).reduce((acc, item) => ({ ...acc, [item.data_id]: item.title }), {})

  const { mutate: createBid } = useElplanBidCreateMutation()
  const { mutate: updateBid } = useElplanBidUpdateMutation()
  const { mutate: copyBidsFromDate } = useCopyBidsFromDate()
  const { mutate: sendBidToElplan, isPending: isSendingBid } = useElplanSendingBidMutation()

  function addNewBlockOrProfileBid(bid: FormattedBid): void {
    createBid(buildDayAheadBidCreatePayload(bid, elplanSnap.elplan_info_id, partitionId))
  }

  function saveBid(bid: FormattedBidWithIdAndTitle): void {
    const mutation = buildDayAheadBidMutation(bid, elplanSnap.elplan_info_id, bid.fake ? bid.partitionId : partitionId)
    if (mutation.operation === 'create') {
      createBid(mutation.payload)
    } else {
      updateBid(mutation.payload)
    }
  }

  function copySpotBidsFromDate(date: ISODateTime, { bidType, partitionId }: BidCopyRequest): void {
    postStat('copy-spotbids-from-date', `click`)

    setBidCopyRequest(undefined)
    copyBidsFromDate(
      buildDayAheadBidCopyPayload(
        elplanSnap.elplan_info_id,
        Datetime.toLocalTime(date, 'onlyDate') as ISODateTime,
        Datetime.toLocalTime(selectedStartTime, 'onlyDate') as ISODateTime,
        bidType,
        partitionId
      )
    )
  }

  const integrationExists = elplanInfo?.integration ?? false
  const { tagsForDate, requireTagForBlockBids, blockBidInputCurrency, profileBidInputCurrency } =
    useDayAheadBidTagSelectionContext({ partitionId })
  const calcDatasets = getCalculatedDataset(
    elplanBidsDatasets,
    getDatasetInstructionsForSumVolumeCalc(amountOfBlockBids)
  )
  const maxBidVolume = elplanSnap?.maxBidVolume
  const bidsIsOvermaxBidVolume =
    typeof maxBidVolume === 'number' ? calcDatasets.values.some((num) => num && num > maxBidVolume) : false

  function handleClearSuccess(contextKeys: string[], withdrawalId: string): void {
    for (const contextKey of contextKeys) {
      clearResetController.reset(contextKey, withdrawalId)
    }
  }

  function closeBlockBidModal(): void {
    setCurrentBlockBid(undefined)
  }

  function closeProfileBidModal(): void {
    setCurrentProfileBid(undefined)
  }

  return (
    <>
      <Card title={t(`Day ahead bid management`)}>
        <div className={styles.DayAheadBidManagement_Period}>
          <div className={styles.DayAheadBidManagement_Period__right}>
            {integrationExists && activeSystem && elplanInfo?.supports_clear && (
              <ClearBidsAction
                date={selectedStartTime}
                systemName={activeSystem.name}
                systemId={activeSystem.id}
                isPreview={activeSystem.is_preview}
                isSuperUser={isSuperUser}
                disabled={disabledBidButtons}
                clearContextKeys={[tableContextKey]}
                onClearSuccess={handleClearSuccess}
              />
            )}
            {integrationExists && (
              <Tooltip
                title={
                  bidsIsOvermaxBidVolume
                    ? t(`Some bids are over the maximum bid volume. Max volume for bids is {{maxBidVolume}} MW`, {
                        maxBidVolume: elplanSnap.maxBidVolume,
                      })
                    : ''
                }
                arrow
              >
                <div className={styles.DayAheadBidManagement_SendBidsButtonContainer}>
                  <NewButton
                    variant="primary"
                    label={t('Send all bids')}
                    disabled={bidsIsOvermaxBidVolume || disabledBidButtons || isSendingBid}
                    loading={isSendingBid}
                    onClick={() => {
                      if (!activeSystem) {
                        return
                      }
                      if (isSuperUser) {
                        setIsBidConfirmOpen(true)
                      } else {
                        sendBidToElplan({
                          date: Datetime.toLocalTime(selectedStartTime, 'onlyDate'),
                          system: activeSystem.name,
                          systemId: activeSystem.id,
                        })
                      }
                    }}
                  />
                  {!disabledBidButtons && (
                    <BidActivityBanner
                      elplanInfoId={elplanSnap.elplan_info_id}
                      startTime={selectedStartTime}
                      endTime={selectedEndTime}
                    />
                  )}
                </div>
              </Tooltip>
            )}
            <TooltipIconButton
              icon="fas fa-cloud-download-alt"
              iconColor={colors.primary5}
              iconSize={'medium'}
              onClick={() => exportPlannedElectricityProductionToExcel(visibleDatasets, datasetNames, selectedDate)}
              tooltip={t('Export to Excel with 15 minute resolution')}
            />
          </div>
        </div>
        {sendIncludesBidsOutsideSelectedPartition && (
          <div className="w-fit">
            <InfoBanner text={t('Send includes bids outside the selected subnet that are not shown here')} />
          </div>
        )}
        <UiConfig
          type="day_ahead_bid_management"
          datasetStartTime={selectedStartTime}
          datasetEndTime={selectedEndTime}
          overrideAlias={partitionId === undefined ? {} : { elplan_partition_id: partitionId }}
          datasetRefreshToken={ELPLAN_BID_REFRESH_EVENT_QUERY_KEY}
        />

        <DayAheadBidInputSurface
          partitionId={partitionId}
          startTime={selectedStartTime}
          endTime={selectedEndTime}
          requireTagForBlockBids={requireTagForBlockBids}
          tableContextKey={tableContextKey}
          tableResetController={clearResetController}
        >
          <DayAheadBidEditor
            datasetStartTime={selectedStartTime}
            datasetEndTime={selectedEndTime}
            tagsForDate={tagsForDate}
            bidInputCurrencies={{
              block: blockBidInputCurrency,
              profile: profileBidInputCurrency,
            }}
            requireTagForBlockBids={requireTagForBlockBids}
            setCurrentBlockBid={setCurrentBlockBid}
            setCurrentProfileBid={setCurrentProfileBid}
            setTypeOfBidToCopy={(bidType, partitionId) => setBidCopyRequest({ bidType, partitionId })}
            disabledBidButtons={disabledBidButtons}
            partitionId={partitionId}
          />
        </DayAheadBidInputSurface>
      </Card>
      {bidCopyRequest !== undefined && (
        <CopyBidsModal
          onSubmit={(chosenDate) => copySpotBidsFromDate(chosenDate, bidCopyRequest)}
          closeModal={() => setBidCopyRequest(undefined)}
        />
      )}
      {currentBlockBid !== undefined && (
        <BidModal
          closeModal={closeBlockBidModal}
          bid={currentBlockBid}
          createBid={addNewBlockOrProfileBid}
          editBid={saveBid}
          type={'block'}
          isEditing={currentBlockBid.fake}
          tagsForDate={tagsForDate}
          requireTagForBlockBids={requireTagForBlockBids}
          bidInputCurrency={blockBidInputCurrency}
        />
      )}
      {currentProfileBid !== undefined && (
        <BidModal
          closeModal={closeProfileBidModal}
          bid={currentProfileBid}
          createBid={addNewBlockOrProfileBid}
          editBid={saveBid}
          type={'profile'}
          isEditing={currentProfileBid.fake}
          tagsForDate={tagsForDate}
          requireTagForBlockBids={requireTagForBlockBids}
          bidInputCurrency={profileBidInputCurrency}
        />
      )}
      {activeSystem && (
        <BidSubmitConfirmDialog
          open={isBidConfirmOpen}
          onClose={() => setIsBidConfirmOpen(false)}
          onConfirm={() => {
            setIsBidConfirmOpen(false)
            sendBidToElplan({
              date: Datetime.toLocalTime(selectedStartTime, 'onlyDate'),
              system: activeSystem.name,
              systemId: activeSystem.id,
            })
          }}
          systemName={activeSystem.name}
          isPreview={activeSystem.is_preview}
        />
      )}
    </>
  )
}
